import { z } from 'zod';

/**
 * Domain model of the Reel Machine.
 *
 * The Script mirrors `script.json` of the production repo (BeatSquares/reel-machine):
 * lines with a beat and an effect track. Three UI fields sit on top of each line:
 * `text` (spoken), `onScreen` (the text card) and `visual` (what the frame shows).
 * `fx` is passed through unchanged to the production planner.
 */

export const Id = z.string().min(1).max(128);

export const Theme = z.object({
  main: z.string(), // brand colour (surface)
  accent: z.string(), // the one accent
  hi: z.string(), // active word in captions
  ground: z.string(),
  ink: z.string(),
  font: z.string(),
  /** Font files of the brand font (URLs at render time, set by Plan from Org.brand.fontFiles). */
  fontFiles: z
    .array(z.object({ src: z.string(), weight: z.string() }))
    .max(4)
    .optional(),
  /** Logo URL at render time (end card). */
  logo: z.string().optional(),
});
export type Theme = z.infer<typeof Theme>;

/** Effect anchor: a word of the line, the whole line, a frame offset, or a reference into another line. */
export const FxAnchor = z.union([
  z.literal('line'),
  z.string(),
  z.number(),
  z.object({ line: z.number().int(), word: z.string().optional(), off: z.number().optional() }),
]);

export const ScriptFx = z
  .object({
    id: z.string(), // catalog id ("snap-zoom") or own element ("tafel", "end-card")
    at: FxAnchor.optional(),
    land: z.number().optional(),
    dur: z.number().optional(),
    until: z.union([z.literal('line'), z.literal('end'), z.object({ line: z.number().int() })]).optional(),
    sfx: z.array(z.object({ src: z.string(), off: z.number().optional(), volume: z.number().optional() })).optional(),
  })
  .passthrough();
export type ScriptFx = z.infer<typeof ScriptFx>;

export const Beat = z.enum(['Hook', 'Konflikt', 'Beweis', 'Wendung', 'Recap', 'CTA']);
export type Beat = z.infer<typeof Beat>;

export const Line = z.object({
  id: Id,
  text: z.string().max(600), // spoken
  beat: z.string().max(40),
  onScreen: z.string().max(200).default(''),
  visual: z.string().max(400).default(''),
  fx: z.array(ScriptFx).default([]),
});
export type Line = z.infer<typeof Line>;

/** Video profile from the effect catalog: Energie, Spiel, Nähe, Grafik, each 0..10. */
export const VideoProfile = z.tuple([
  z.number().min(0).max(10),
  z.number().min(0).max(10),
  z.number().min(0).max(10),
  z.number().min(0).max(10),
]);

/** Where an image came from. upload: the user; source: the read page; person: official portrait; stock: Unsplash;
 * wiki: free photo of a named place or thing (Wikipedia, Wikimedia Commons);
 * screenshot: the source page with the claim marked (proof-screenshot). */
export const AssetKind = z.enum(['upload', 'source', 'person', 'stock', 'wiki', 'screenshot']);
export type AssetKind = z.infer<typeof AssetKind>;

export const Asset = z.object({
  key: z.string(),
  name: z.string(),
  contentType: z.string(),
  kind: AssetKind.optional(),
  /** Shown on the image in the video ("Foto: Name/Unsplash"). */
  credit: z.string().max(200).optional(),
  /** What the image shows (for the script writer). */
  alt: z.string().max(400).optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  /** Where the image came from. Stock images are hotlinked from here instead of a copy in the bucket. */
  sourceUrl: z.string().max(2000).optional(),
});
export type Asset = z.infer<typeof Asset>;

export const Script = z.object({
  version: z.number().int().nonnegative(),
  lang: z.string().default('de'),
  title: z.string().max(200),
  profile: VideoProfile.optional(),
  theme: Theme.optional(),
  face: z.object({ x: z.number(), y: z.number(), chin: z.number() }).optional(),
  music: z.object({ src: z.string(), volume: z.number(), endVolume: z.number().optional() }).optional(),
  end: z
    .object({
      frames: z.number().int().nonnegative(),
      title: z.string(),
      sub: z.string().optional(),
      lines: z.array(z.string()).default([]),
    })
    .optional(),
  lines: z.array(Line).min(1).max(40),
  /** Images the run collected for this script (source, people, stock, screenshots). Effects use their keys as src. */
  images: z.array(Asset).max(40).optional(),
});
export type Script = z.infer<typeof Script>;

/** Claims register: every statement in the film has a source. */
export const Claim = z.object({ statement: z.string(), evidence: z.string(), wording: z.string() });
export type Claim = z.infer<typeof Claim>;

export const Concept = z.object({
  key: z.string(), // "A" | "B" | "C"
  name: z.string(), // e.g. "ABT", "Rätsel", "Mensch mit Ziel"
  hook: z.string(),
  score: z.number().min(0).max(10),
  reason: z.string().default(''),
});
export type Concept = z.infer<typeof Concept>;

export const Stage = z.enum(['script', 'record', 'video', 'post', 'stats']);
export type Stage = z.infer<typeof Stage>;
export const STAGES: readonly Stage[] = Stage.options;

export const JobKind = z.enum(['channel', 'script', 'transcribe', 'render', 'publish']);
export type JobKind = z.infer<typeof JobKind>;

/** Where a failure belongs (review: error taxonomy that points to the owner of the fix). */
export const FailureInfo = z.object({
  code: z.string().max(80), // e.g. "provider_timeout", "claims_violation", "stale_run"
  severity: z.enum(['info', 'warning', 'error', 'critical']),
  boundary: z.enum(['authority', 'state', 'verification', 'evaluation', 'provider', 'infrastructure']),
});
export type FailureInfo = z.infer<typeof FailureInfo>;

/**
 * A running or failed job. `runId` is the fencing token: every write by a pipeline task must match the
 * reel's current `job.runId` (`Store.updateReelFenced`), so a late or old execution can never overwrite
 * newer work (review F05). `generation` increases with every claim.
 */
export const Job = z.object({
  kind: JobKind,
  status: z.enum(['running', 'failed']),
  runId: z.string().optional(),
  generation: z.number().int().nonnegative().optional(),
  step: z.string().optional(),
  progress: z.number().min(0).max(1).optional(),
  error: z.string().optional(),
  failure: FailureInfo.optional(),
  executionArn: z.string().optional(),
  startedAt: z.string(),
  /** Last sign of life from the execution; stale detection uses it instead of a fixed age. */
  heartbeatAt: z.string().optional(),
});
export type Job = z.infer<typeof Job>;

export const Take = z.object({
  n: z.number().int().positive(),
  key: z.string(),
  /** The upload this take was finalized from (review F09). Immutable object identity: key + ETag. */
  uploadId: z.string().optional(),
  etag: z.string().optional(),
  /** S3 object version accepted at finalize. Every later read is bound to it (recheck R07). */
  versionId: z.string().optional(),
  sizeBytes: z.number().int().nonnegative().optional(),
  contentType: z.string(),
  durationMs: z.number().int().nonnegative(),
  scriptVersion: z.number().int().nonnegative(),
  transcriptKey: z.string().optional(),
  createdAt: z.string(),
  createdBy: z.string(),
});
export type Take = z.infer<typeof Take>;

/**
 * Quality of one render (review F04/F06). Starts "pending" when the MP4 exists; only "passed" makes it
 * publishable. `assessedDigest` must equal the render's `digest`, so an assessment of render A can never
 * authorize render B.
 */
export const RenderQuality = z.object({
  status: z.enum(['pending', 'passed', 'failed', 'unchecked']),
  assessedDigest: z.string().optional(),
  /** The caption is part of the checked artifact: a caption edit needs a recheck. */
  assessedCaptionDigest: z.string().optional(),
  /** Final-artifact fidelity: spoken audio, overlays, end card and caption against the claims register. */
  fidelity: z
    .object({
      passed: z.boolean(),
      claimViolations: z.array(z.string()).default([]),
      checked: z.array(z.enum(['speech', 'overlays', 'endCard', 'caption'])).default([]),
      missing: z.array(z.string()).default([]),
    })
    .optional(),
  /** Technical and visual checks; failures here are reviewable warnings unless marked hard. */
  video: z
    .object({
      passed: z.boolean(),
      hardFailures: z.array(z.string()).default([]),
      warnings: z.array(z.string()).default([]),
    })
    .optional(),
  /** A staff override of reviewable warnings, with reason. Never covers factual failures. */
  override: z.object({ by: z.string(), reason: z.string().min(10).max(1000), at: z.string() }).optional(),
  updatedAt: z.string(),
});
export type RenderQuality = z.infer<typeof RenderQuality>;

export const Render = z.object({
  version: z.number().int().positive(),
  key: z.string(), // S3 key of the MP4 in the media bucket, run-scoped and immutable
  durationS: z.number().nonnegative(),
  scriptVersion: z.number().int().nonnegative(),
  take: z.number().int().positive(),
  /** Production manifest this render was made from (review: one manifest connects approval, production, learning). */
  manifestId: z.string().optional(),
  runId: z.string().optional(),
  /** Content digest of the MP4 (sha256 or S3 ETag). */
  digest: z.string().optional(),
  quality: RenderQuality.optional(),
  renderedAt: z.string(),
});
export type Render = z.infer<typeof Render>;

export const Metrics = z.object({
  views: z.number().int().nonnegative(),
  reach: z.number().int().nonnegative(),
  avgWatchPct: z.number().min(0).max(100),
  saves: z.number().int().nonnegative(),
  likes: z.number().int().nonnegative().optional(),
  comments: z.number().int().nonnegative().optional(),
  shares: z.number().int().nonnegative().optional(),
  updatedAt: z.string(),
});
export type Metrics = z.infer<typeof Metrics>;

export const Source = z.discriminatedUnion('type', [
  z.object({ type: z.literal('link'), value: z.string().url() }),
  z.object({ type: z.literal('text'), value: z.string().min(20).max(50_000) }),
]);
export type Source = z.infer<typeof Source>;

/** Web address of a source: the link, or the "Quelle: <url>" line of a pasted text (archive imports). */
export function sourceUrl(source: Source): string | undefined {
  if (source.type === 'link') return source.value;
  const m = /(?:Quelle|Source)\s*:\s*(https?:\/\/\S+)/i.exec(source.value);
  return m?.[1];
}

export const Reel = z.object({
  id: Id,
  orgId: Id,
  title: z.string(),
  stage: Stage,
  job: Job.optional(),
  source: Source,
  assets: z.array(Asset).default([]),
  concept: Concept.optional(),
  alternatives: z.array(Concept).default([]),
  takes: z.array(Take).default([]),
  selectedTake: z.number().int().positive().optional(),
  render: Render.optional(),
  caption: z.string().max(2200).optional(),
  post: z.object({ zernioPostId: z.string(), url: z.string().optional(), postedAt: z.string() }).optional(),
  /** Durable publication intent and its provider state (review F03). */
  publication: z.lazy(() => Publication).optional(),
  /**
   * Store-managed revision, +1 on every write of the reel item. The compare-and-set token for
   * read-decide-write (recheck: timestamps are not revisions). Callers never set it.
   */
  rev: z.number().int().nonnegative().optional(),
  /** Increases with every job claim; the fencing generation. */
  generation: z.number().int().nonnegative().optional(),
  metrics: Metrics.optional(),
  /** Which playbook produced this reel (set when the script pipeline starts). See learning.ts. */
  playbook: z
    .object({
      global: z.number().int().positive(),
      org: z.number().int().positive().optional(),
      served: z.object({ scope: z.string(), version: z.number().int().positive() }),
      experimentId: z.string().optional(),
      arm: z.enum(['control', 'treatment']).optional(),
    })
    .optional(),
  /** Technical and visual check of the rendered video (set after render). Shape: learning.ts VideoQa. */
  videoQa: z
    .object({
      passed: z.boolean(),
      checks: z.array(
        z.object({
          id: z.string(),
          ok: z.boolean(),
          detail: z.string().default(''),
          frame: z.number().int().optional(),
        }),
      ),
      stillKeys: z.array(z.string()).default([]),
      checkedAt: z.string(),
    })
    .optional(),
  /** Fidelity of the finished video to the message (set after render). See learning.ts. */
  fidelity: z
    .object({
      coreMean: z.number().min(0).max(2),
      /** Metric version: 2 = yes/no per core statement (Kernsatz + fact budget), neutral judges. */
      metricVersion: z.number().int().positive().default(2),
      passed: z.boolean(),
      claimViolations: z.number().int().nonnegative(),
      misunderstandings: z.array(z.string()).default([]),
      checkedAt: z.string(),
    })
    .optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  createdBy: z.string(),
});
export type Reel = z.infer<typeof Reel>;

export const ChannelProfile = z.object({
  summary: z.string(), // tone in one or two sentences
  patterns: z.array(z.string()).default([]), // at least three concrete patterns
  bestLengthS: z.tuple([z.number(), z.number()]).optional(),
  reelsAnalyzed: z.number().int().nonnegative(),
  readAt: z.string(),
});
export type ChannelProfile = z.infer<typeof ChannelProfile>;

export const Org = z.object({
  id: Id,
  name: z.string(),
  instagramHandle: z
    .string()
    .regex(/^@?[A-Za-z0-9._]{1,30}$/)
    .optional(),
  website: z.string().optional(),
  brand: z
    .object({
      theme: Theme,
      logoKey: z.string().optional(),
      /** Self-hosted brand font of the website, copied to orgs/<id>/brand/ (keys and CSS weights). */
      fontFiles: z
        .array(z.object({ key: z.string(), weight: z.string() }))
        .max(4)
        .optional(),
    })
    .optional(),
  /** Instagram profile picture, copied to the media bucket when the channel is read. */
  avatarKey: z.string().optional(),
  /** Presigned URL of `avatarKey`, set by the API on read. Never stored. */
  avatarUrl: z.string().optional(),
  channel: ChannelProfile.optional(),
  zernio: z
    .object({
      profileId: z.string(),
      accountId: z.string().optional(),
      status: z.enum(['pending', 'connected', 'expired']),
      connectedAt: z.string().optional(),
    })
    .optional(),
  job: Job.optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Org = z.infer<typeof Org>;

export const Role = z.enum(['admin', 'editor']);
export const Membership = z.object({ orgId: Id, userId: Id, role: Role, email: z.string().optional() });
export type Membership = z.infer<typeof Membership>;

export const Actor = z.object({
  type: z.enum(['user', 'system', 'panel']),
  id: z.string(),
  name: z.string(),
});
export type Actor = z.infer<typeof Actor>;

/** One entry of a reel's history. Every edit, run and decision lands here. */
export const ReelEvent = z.object({
  id: Id,
  reelId: Id,
  at: z.string(),
  actor: Actor,
  type: z.string(), // e.g. "script.generated", "script.edited", "chat", "take.recorded", "render.done"
  message: z.string(),
  data: z.record(z.unknown()).optional(),
});
export type ReelEvent = z.infer<typeof ReelEvent>;

// ---------- publication (review F03) ----------

/**
 * Publishing is an external, non-idempotent effect. The API only creates an intent (atomically, one open
 * intent per reel); a durable worker owns the attempt. A lost provider response leads to "unknown" and
 * reconciliation, never to an automatic resubmission.
 */
export const PublicationState = z.enum([
  'intent', // persisted, not yet sent
  'submitting', // worker owns the attempt
  'unknown', // provider outcome ambiguous: reconcile, do not resend
  'accepted', // provider accepted, processing
  'published', // terminal: confirmed live
  'rejected', // terminal: provider refused
  'cancelled', // terminal: intent withdrawn before submission
]);
export type PublicationState = z.infer<typeof PublicationState>;

export const Publication = z.object({
  intentId: Id, // stable client operation id, also the idempotency/reference key sent to the provider
  state: PublicationState,
  manifestId: z.string(),
  renderVersion: z.number().int().positive(),
  renderDigest: z.string(),
  captionDigest: z.string(),
  accountId: z.string(),
  attempts: z
    .array(
      z.object({
        at: z.string(),
        outcome: z.enum(['sent', 'accepted', 'rejected', 'error', 'timeout']),
        detail: z.string().max(500).default(''),
      }),
    )
    .default([]),
  providerPostId: z.string().optional(),
  url: z.string().optional(),
  error: z.string().optional(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Stable id of the client request that created the intent; a repeated request returns the same intent. */
  requestId: z.string().max(100).optional(),
  /** Set when a person resolved an "unknown" state by hand. */
  resolution: z.object({ by: z.string(), at: z.string(), note: z.string() }).optional(),
});
export type Publication = z.infer<typeof Publication>;
